import { phoneNumberSchema, type CallFlowSummary, type Me, type PhoneNumberSummary, type QueueSummary, type RegionSummary, type TrunkSummary, type UserSummary } from '@smarton-pbx/shared';
import { Hash, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../components/ui';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { rich, t } from '../i18n';
import { formatNumber } from '../lib/calls';
import { useLoad, useSubmit } from '../lib/hooks';
import { SettingsTabs } from './SettingsTabs';

/** Where a number's calls go, in words. */
function rings(number: PhoneNumberSummary): string {
  if (number.userName) return number.userName;
  if (number.queueName) return t('Queue: {name}', { name: number.queueName });
  if (number.flowName) return t('Flow: {name}', { name: number.flowName });
  return t('Shared');
}

function numberColumns(): Column<PhoneNumberSummary>[] {
  return [
    {
      key: 'number',
      label: t('Number'),
      fixed: true,
      cell: (number) => (
        <>
          <div className="name tabular">
            {formatNumber(number.e164)}
            {number.texts && (
              <>
                {' '}
                <Pill>{number.textQueueName ? t('Texts: {name}', { name: number.textQueueName }) : t('Texts')}</Pill>
              </>
            )}
          </div>
          {number.label && <div className="detail subtle">{number.label}</div>}
        </>
      ),
      excel: [
        { label: t('Number'), value: (number) => number.e164 },
        { label: t('Label'), value: (number) => number.label },
      ],
    },
    {
      key: 'rings',
      label: t('Rings'),
      cell: (number) =>
        number.userName ?? (number.queueName || number.flowName ? <Pill kind="brand">{rings(number)}</Pill> : <Pill>{rings(number)}</Pill>),
      value: rings,
    },
    { key: 'region', label: t('Region'), className: 'hide-mobile muted', cell: (number) => number.regionName, value: (number) => number.regionName },
    { key: 'carrier', label: t('Carrier'), className: 'hide-mobile muted', cell: (number) => number.trunkName, value: (number) => number.trunkName },
    {
      key: 'texts',
      label: t('Texts'),
      hidden: true,
      className: 'hide-mobile muted',
      cell: (number) => (number.texts ? (number.textQueueName ?? t('On')) : t('Off')),
      value: (number) => (number.texts ? (number.textQueueName ?? t('On')) : t('Off')),
    },
  ];
}

export function Numbers({ me }: { me: Me }) {
  const isAdmin = me.role === 'TENANT_ADMIN';
  const numbers = useLoad(api.listNumbers);
  const table = useTable('numbers', numberColumns(), mayDownload(me.role) ? { name: t('Numbers'), rows: () => numbers.data ?? [] } : null);
  const options = useLoad(
    () => (isAdmin ? Promise.all([api.listTrunks(), api.listRegions(), api.listUsers(), api.listQueues(), api.listFlows()]) : Promise.resolve(null)),
    [isAdmin],
  );
  const [editing, setEditing] = useState<PhoneNumberSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();
  const [trunks, regions, users, queues, flows] = options.data ?? [[], [], [], [], []];

  async function remove(number: PhoneNumberSummary) {
    if (!confirm(t('Remove {number}? Calls to it will no longer reach anyone.', { number: formatNumber(number.e164) }))) return;
    try {
      await api.deleteNumber(number.id);
      numbers.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const canAdd = isAdmin && trunks.length > 0 && regions.length > 0;

  return (
    <div className="stack">
      <SettingsTabs admin={isAdmin} />
      <PageHeader
        title={t('Numbers')}
        description={t('Phone numbers from your carriers, and where each leads: a person, a queue or a call flow. A person’s number is also what customers see when they call.')}
      >
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')} disabled={!canAdd} data-testid="add-number">
            <Plus size={16} aria-hidden /> {t('Add number')}
          </button>
        )}
      </PageHeader>
      {isAdmin && options.data && trunks.length === 0 && (
        <Notice>
          {rich('Numbers come from a carrier. {link} first.', { link: <Link to="/settings/carriers">{t('Add your carrier')}</Link> })}
        </Notice>
      )}
      {(error ?? numbers.error ?? options.error) && <Notice kind="error">{error ?? numbers.error ?? options.error}</Notice>}
      {!numbers.data ? (
        <Loading />
      ) : numbers.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Hash} title={t('No numbers yet')}>
            <p>{t('Add the numbers your carrier gave you, and choose who each one rings.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {numbers.data.map((number) => (
                <tr key={number.id} data-testid="number-row" {...(isAdmin && opensOnClick(() => setEditing(number)))}>
                  <Cells table={table} row={number} />
                  <td className="actions">
                    {isAdmin && (
                      <Menu
                        label={t('Actions for {name}', { name: number.e164 })}
                        trigger={(props) => (
                          <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: number.e164 })} {...props}>
                            <MoreHorizontal size={18} aria-hidden />
                          </button>
                        )}
                      >
                        {(close) => (
                          <>
                            <button type="button" role="menuitem" onClick={() => (close(), setEditing(number))}>
                              <Pencil size={16} aria-hidden /> {t('Edit')}
                            </button>
                            <button type="button" role="menuitem" className="danger-item" onClick={() => (close(), void remove(number))}>
                              <Trash2 size={16} aria-hidden /> {t('Remove')}
                            </button>
                          </>
                        )}
                      </Menu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="subtle">
        {t(
          'Calls out use the caller’s own number. People without one call out with their region’s shared number; without that, they can call colleagues only.',
        )}
      </p>
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('Add number') : editing ? t('Edit {name}', { name: formatNumber(editing.e164) }) : ''}
      >
        {editing && (
          <NumberForm
            number={editing === 'new' ? null : editing}
            trunks={trunks}
            regions={regions}
            users={users}
            queues={queues}
            flows={flows}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              numbers.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function NumberForm({
  number,
  trunks,
  regions,
  users,
  queues,
  flows,
  onCancel,
  onSaved,
}: {
  number: PhoneNumberSummary | null;
  trunks: TrunkSummary[];
  regions: RegionSummary[];
  users: UserSummary[];
  queues: QueueSummary[];
  flows: CallFlowSummary[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [e164, setE164] = useState(number?.e164 ?? '+');
  const [trunkId, setTrunkId] = useState(number?.trunkId ?? trunks[0]?.id ?? '');
  const [regionId, setRegionId] = useState(number?.regionId ?? regions[0]?.id ?? '');
  // "user:<id>", "queue:<id>", "flow:<id>" or "" (the region's shared number).
  const [target, setTarget] = useState(
    number?.userId ? `user:${number.userId}` : number?.queueId ? `queue:${number.queueId}` : number?.flowId ? `flow:${number.flowId}` : '',
  );
  const [label, setLabel] = useState(number?.label ?? '');
  // "" (off), "calls" (where its calls go) or "queue:<id>".
  const [texts, setTexts] = useState(number?.texts ? (number.textQueueId ? `queue:${number.textQueueId}` : 'calls') : '');
  const form = useSubmit();
  const trunk = trunks.find((t) => t.id === trunkId);
  // The region's people, and admins of all regions (their calls then follow the number's region).
  const people = users.filter((user) => (user.regionId === regionId || user.regionId === null) && !user.disabled);
  const regionQueues = queues.filter((queue) => queue.regionId === regionId);
  const regionFlows = flows.filter((flow) => flow.regionId === regionId);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const [kind, id] = target.split(':');
    const input = {
      e164: e164.replace(/[\s()-]/g, ''),
      trunkId,
      regionId,
      userId: kind === 'user' ? id! : null,
      queueId: kind === 'queue' ? id! : null,
      flowId: kind === 'flow' ? id! : null,
      label: label.trim() || null,
      texts: texts !== '',
      textQueueId: texts.startsWith('queue:') ? texts.slice('queue:'.length) : null,
    };
    const saved = await form.submit(phoneNumberSchema, input, (value) => (number ? api.updateNumber(number.id, value) : api.createNumber(value)));
    if (saved) onSaved();
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body">
        <div className="form-grid">
          <Field label={t('Number')} hint={t('With the country code, e.g. +370 5 200 0101')} error={form.fields.e164}>
            <input inputMode="tel" value={e164} onChange={(e) => setE164(e.target.value)} autoFocus data-testid="number-e164" />
          </Field>
          <Field label={t('Carrier')} error={form.fields.trunkId}>
            <select value={trunkId} onChange={(e) => setTrunkId(e.target.value)} data-testid="number-trunk">
              {trunks.map((trunk) => (
                <option key={trunk.id} value={trunk.id}>
                  {trunk.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Region')} error={form.fields.regionId}>
            <select
              value={regionId}
              onChange={(e) => {
                setRegionId(e.target.value);
                setTarget('');
              }}
              data-testid="number-region"
            >
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={t('Rings')}
            hint={t('A person, a queue, a call flow, or nobody: a shared number is the region’s, and people without their own number call out with it')}
            error={form.fields.userId ?? form.fields.queueId ?? form.fields.flowId}
          >
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="number-user">
              <option value="">{t('Shared number of the region')}</option>
              {regionFlows.length > 0 && (
                <optgroup label={t('Call flows')}>
                  {regionFlows.map((flow) => (
                    <option key={flow.id} value={`flow:${flow.id}`}>
                      {flow.published ? flow.name : t('{name} (not published)', { name: flow.name })}
                    </option>
                  ))}
                </optgroup>
              )}
              {regionQueues.length > 0 && (
                <optgroup label={t('Queues')}>
                  {regionQueues.map((queue) => (
                    <option key={queue.id} value={`queue:${queue.id}`}>
                      {queue.name}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={t('People')}>
                {people.map((user) => (
                  <option key={user.id} value={`user:${user.id}`}>
                    {user.regionId === null
                      ? t('{name} (ext. {extension}, all regions)', { name: user.name, extension: user.extension })
                      : t('{name} (ext. {extension})', { name: user.name, extension: user.extension })}
                  </option>
                ))}
              </optgroup>
            </select>
          </Field>
          <Field label={t('Label (optional)')} hint={t('For your own reference, e.g. Sales line')} error={form.fields.label}>
            <input value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Field
            label={t('Texts')}
            hint={trunk?.texts ? t('Texts (SMS) to the number, and replies from it') : t('Its carrier doesn’t pass texts. Turn them on for the carrier first.')}
            error={form.fields.texts ?? form.fields.textQueueId}
          >
            <select value={trunk?.texts ? texts : ''} onChange={(e) => setTexts(e.target.value)} disabled={!trunk?.texts} data-testid="number-texts">
              <option value="">{t('Off')}</option>
              <option value="calls">
                {target.startsWith('user:') || target.startsWith('queue:')
                  ? t('Where its calls go')
                  : t('To the admins and the region’s managers')}
              </option>
              {regionQueues.length > 0 && (
                <optgroup label={t('To a queue')}>
                  {regionQueues.map((queue) => (
                    <option key={queue.id} value={`queue:${queue.id}`}>
                      {queue.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
        </div>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-number">
          {number ? t('Save changes') : t('Add number')}
        </button>
      </div>
    </form>
  );
}
